import { Add, Delete, DoneAll, RemoveDone, MenuBook, ExpandMore } from '@mui/icons-material';
import { AccordionDetails, AccordionSummary, Grid, IconButton, Typography } from '@mui/material';
import { Form, getIn, useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import useSWR from 'swr';
import { IWorkflow } from '@modules/admin/AdminTools/Workflows/types';
import { FormPart } from '@modules/shared/components/FormPart';

import { ILeadSource } from '@modules/ContactManager/components/Tabs/components/Leads/types/interfaces/interfaces';
import { getOptions } from '@shared/commonFunctions';
import { statesList } from '@shared/constants/statesList';
import { CheckboxField } from '@shared/fields/CheckboxField';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { useLeadSource } from '@shared/hooks/useLeadSource';

import { workflowStepTreeOptions } from '@modules/admin/AdminTools/Workflows/utils';
import { SaveButton } from '@shared/inputs/Buttons';
import { CancelButton } from '@styles/StyledButtons';
import { DictionaryAccordianWrapper } from '@styles/CommonStyles';
import { GridItem } from '@shared/styledComponents';
//import { ILenderListItem } from '../../shared/LenderControlsHeader';
import { SERVICES } from '@shared/constants/apiConfig';
import { TextEditorField } from '@shared/fields/TextEditorField';
import { ICommunicationValues, IEmail, IEmailTemplates, ISid, TCommunicationFormConfig, ISMSTemplates } from '../types';


const ErrorWrapper = styled.div<{ top?: string; left?: string }>`
  color: rgb(211, 47, 47);
  font-weight: 400;
  font-size: 0.75rem;
  position: sticky;
  top: ${({ top }) => top};
  left: ${({ left }) => left};
`;

const StyledFormPart = styled.div`
  > div:last-child {
    .MuiFormHelperText-root {
      color: transparent;
    }
  }
`;

const DropdownsWrapper = styled.div`
  display: flex;
`;

export const ReadOnlyPreview = styled.div`
  border-bottom: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 13px 12px;
  margin-top: 4px;
  margin-bottom: 25px;
  color: rgb(0, 0, 0);
  font-size: 11px;
  font-weight: 600;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const PreviewLabel = styled.div`
  color: rgb(0, 0, 0);
  font-size: 11px;
  font-weight: 600;
  margin-top: 8px;
  margin-bottom: 2px;
  padding-left: 10px;
`;

const isSmsConfig: TCommunicationFormConfig = [
  {
    name: 'isSms',
    label: 'SMS',
    component: CheckboxField,
  },
];

const isAdverseActionConfig: TCommunicationFormConfig = [
  {
    name: 'isAdverseAction',
    label: 'Adverse Action',
    component: CheckboxField,
  },
];

const getSmsTextConfig = (sids: ISid[], isLoadedWithError: boolean, smsTemplates: ISMSTemplates): TCommunicationFormConfig => [
  {
    width: '100%',
    name: 'fromSid',
    options: getOptions(sids, 'id', 'sid', 'source'),
    isLoading: !sids && !isLoadedWithError,
    component: SelectAutocompleteField,
  },
  {
    width: '100%',
    name: 'smsTemplateId',
    label: 'Select SMS Template',
    component: SelectAutocompleteField,
    options: smsTemplates.map((t) => ({ label: t.name, value: t.id })),
  },
];

const getEmailFormConfig = (
  emails: IEmail[],
  isLoadedWithError: boolean,
  emailTemplates: IEmailTemplates
): TCommunicationFormConfig => [
  {
    width: '100%',
    name: 'fromEmail',
    options: getOptions(emails, 'id', 'email'),
    isLoading: !emails && !isLoadedWithError,
    component: SelectAutocompleteField,
  },
  {
    width: '100%',
    name: 'emailTemplateId',
    label: 'Select Email Template',
    component: SelectAutocompleteField,
    options: emailTemplates.map((t) => ({ label: t.name, value: t.id })),
  },
  {
    width: '100%',
    name: 'emailSubject',
    disabled: true,
  },
  {
    width: '100%',
    name: 'emailTemplate',
    label: 'Email Body',
    component: TextEditorField,
    disable: true,
  },
];

const isSafeToContactConfig: TCommunicationFormConfig = [
  {
    name: 'isSafeToContact',
    label: 'Safe to Contact',
    component: CheckboxField,
  },
];

const isEmailConfig: TCommunicationFormConfig = [
  {
    name: 'isEmail',
    label: 'Email',
    component: CheckboxField,
  },
];

const delayFormConfig: TCommunicationFormConfig = [
  {
    width: '100%',
    name: 'delayInMinutes',
    label: 'Delay (minutes)',
    component: NumberInputField,
    removeThousandSeparator: true,
  },
];

const statesFormConfig: TCommunicationFormConfig = [
  {
    width: '100%',
    name: 'excludedStates',
    component: SelectAutocompleteField,
    margin: '10px 0 0 0',
    options: statesList,
    multiple: true,
  },
];

const getWorkflowFormConfig = (
  workflowIdValue: string | null,
  index: number,
  workflowTree: IWorkflow[],
  setFieldValue: <T>(field: string, value: T, shouldValidate?: boolean) => void
): TCommunicationFormConfig => {
  const workflowOptions = workflowTree.map(({ workflowName, workflowId }) => ({
    label: workflowName,
    value: workflowId,
  }));

  const currentWorkflow = workflowTree.find(({ workflowId }) => workflowId === workflowIdValue);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const shouldSelectComponentUpdate = (nextProps: any, currentProps: any) => {
    return (
      nextProps.name !== currentProps.name ||
      nextProps.required !== currentProps.required ||
      nextProps.disabled !== currentProps.disabled ||
      nextProps.readOnly !== currentProps.readOnly ||
      nextProps.formik.isSubmitting !== currentProps.formik.isSubmitting ||
      Object.keys(nextProps).length !== Object.keys(currentProps).length ||
      getIn(nextProps.formik.values, currentProps.name) !== getIn(currentProps.formik.values, currentProps.name) ||
      getIn(nextProps.formik.errors, currentProps.name) !== getIn(currentProps.formik.errors, currentProps.name) ||
      getIn(nextProps.formik.touched, currentProps.name) !== getIn(currentProps.formik.touched, currentProps.name) ||
      JSON.stringify(nextProps.options) !== JSON.stringify(currentProps.options)
    );
  };

  return [
    {
      width: '35%',
      isErrorShown: false,
      name: `workflowIds.${index}`,
      label: 'Select Workflow',
      component: SelectAutocompleteField,
      options: workflowOptions,
      onChange: (value: string) => {
        if (!value) {
          setFieldValue(`workflowSteps.${index}`, '');
        }
      },
    },
    {
      width: '65%',
      isErrorShown: false,
      name: `workflowSteps.${index}`,
      label: 'Select Workflow Step',
      component: SelectAutocompleteField,
      options: workflowStepTreeOptions(currentWorkflow?.workflowSteps),
      shouldUpdate: shouldSelectComponentUpdate,
      disabled: !workflowIdValue,
    },
  ];
};

interface IProps {
  sids: ISid[];
  emails: IEmail[];
  emailTemplates: IEmailTemplates;
  smsTemplates: ISMSTemplates;
  isSidLoadedWithError: boolean;
  isEmailLoadedWithError: boolean;
  workflowTree: IWorkflow[];
}

export const EditCommunicationForm = ({
  workflowTree,
  isSidLoadedWithError,
  isEmailLoadedWithError,
  sids,
  emails,
  emailTemplates,
  smsTemplates,
}: IProps) => {
  const { leadSourceData: sources } = useLeadSource<ILeadSource[]>();

  //const { data: lenderList } = useSWR<ILenderListItem[]>(`/${SERVICES.LENDER}/lenders`);
  const { data: dictionary } = useSWR<string[]>(`/${SERVICES.WORKFLOW}/communications/get-dictionary-variables`);

  // const formattedLendersList: IOption[] = useMemo(() =>
  //   lenderList ? lenderList.map(lender => ({ label: lender.name, value: lender.id })) : [],
  //   [lenderList]
  // );
  // Todo: to be used in future.
  // const lenderFormConfig = useMemo(() => [
  //   {
  //     width: '100%',
  //     name: 'includedLenders',
  //     component: SelectAutocompleteField,
  //     margin: '10px 0 0 0',
  //     options: formattedLendersList,
  //     multiple: true,
  //   }
  // ], [formattedLendersList]);


  const leadSourcesFormConfig = useMemo(
    () => [
      {
        width: '100%',
        name: 'includedLeadSources',
        component: SelectAutocompleteField,
        margin: '10px 0 0 0',
        options: getOptions(sources),
        multiple: true,
        size: 'small',
        checkboxes: true,
      },
    ],
    [sources]
  );

  const { values, isSubmitting, dirty, handleReset, setFieldValue, setValues, errors } = useFormikContext<ICommunicationValues>();

  const onSelectAllStates = useCallback(
    (selected: boolean): void => {
      const updatedExcludedStates = !selected ? [] : statesList.map(({ value }) => value);
      setFieldValue('excludedStates', updatedExcludedStates);
    },
    [setFieldValue]
  );

  const onSelectAllSources = useCallback(
    (selected: boolean): void => {
      const updatedIncludedSources = !selected ? [] : sources?.map((source) => source.id);
      setFieldValue('includedLeadSources', updatedIncludedSources);
    },
    [sources]
  );

  const selectedEmailTemplate = emailTemplates.find((t) => t.id === values.emailTemplateId) ?? null;
  const selectedSmsTemplate = smsTemplates.find((t) => t.id === values.smsTemplateId) ?? null;
  useEffect(() => {
    if (!values.isEmail) return;

    if (selectedEmailTemplate) {
      setFieldValue('emailTemplateId', selectedEmailTemplate.id);
      setFieldValue('emailSubject', selectedEmailTemplate.subject);
      setFieldValue('emailTemplate', {
        html: selectedEmailTemplate.templateData,
        plainText: selectedEmailTemplate.templateData,
      });
      setFieldValue('selectedEmailTemplate', selectedEmailTemplate);
    } else {
      setFieldValue('emailTemplateId', null);
    }
  }, [values.emailTemplateId, values.isEmail, selectedEmailTemplate]);

  useEffect(() => {
    if (!values.isSms) return;

    if (selectedSmsTemplate) {
      setFieldValue('smsTemplateId', selectedSmsTemplate.id);
      setFieldValue('smsTemplate', selectedSmsTemplate.body);
      setFieldValue('selectedSMSTemplate', selectedSmsTemplate);
    } else {
      setFieldValue('smsTemplateId', null);
    }
  }, [values.smsTemplateId, values.isSms, selectedSmsTemplate]);

  useEffect(() => {
    if (!values.isEmail) {
      setFieldValue('emailTemplateId', null);
      setFieldValue('emailSubject', '');
      setFieldValue('emailTemplate', {
        html: '',
        plainText: '',
      });
      setFieldValue('selectedEmailTemplate', null);
    }
  }, [values.isEmail]);

  useEffect(() => {
    if (!values.isSms) {
      setFieldValue('smsTemplateId', null);
      setFieldValue('isSafeToContact', false);
      setFieldValue('smsTemplate', '');
      setFieldValue('selectedSMSTemplate', null);
    }
  }, [values.isSms]);

  const onAddWorkflowStep = useCallback((): void => {
    const updatedValues: ICommunicationValues = {
      ...values,
      workflowIds: [...values.workflowIds, null],
      workflowSteps: [...values.workflowSteps, null],
    };
    setValues(updatedValues);
  }, [values, setValues]);

  const onRemoveWorkflowStep = useCallback(
    (removedIndex: number): void => {
      const updatedValues: ICommunicationValues = {
        ...values,
        workflowIds: values.workflowIds.filter((_, index) => index !== removedIndex),
        workflowSteps: values.workflowSteps.filter((_, index) => index !== removedIndex),
      };
      setValues(updatedValues);
    },
    [values, setValues]
  );

  const isDisableNewWorkflowStep = useMemo<boolean>(() => {
    const { workflowIds, workflowSteps } = values;
    const lastWorkflowIdx = workflowIds.length - 1;
    return !workflowIds[lastWorkflowIdx] || !workflowSteps[lastWorkflowIdx];
  }, [values]);



  return (
    <Form>
      <Grid
        container
        justifyContent="flex-end"
        columnSpacing={3}
      >
        <Grid
          item
          xs={12}
          md={2}
        >
          <FormPart config={isSmsConfig} />
          <ErrorWrapper>{errors.isSms}</ErrorWrapper>

          <FormPart config={isEmailConfig} />

          <FormPart config={isAdverseActionConfig} />
          <ErrorWrapper>{errors.isAdverseAction}</ErrorWrapper>
        </Grid>

        <Grid
          item
          xs={12}
          md={10}
          marginBottom={1}
        >
          {values.isSms && (
            <>
              <FormPart config={getSmsTextConfig(sids, isSidLoadedWithError, smsTemplates)} />
              <PreviewLabel>SMS Template</PreviewLabel>
              <ReadOnlyPreview>{values?.selectedSMSTemplate?.body}</ReadOnlyPreview>
              <FormPart config={isSafeToContactConfig} />
            </>
          )}

          {values.isEmail && (
            <>
              <FormPart config={getEmailFormConfig(emails, isEmailLoadedWithError, emailTemplates)} />
            </>
          )}
        </Grid>

        <Grid
          item
          xs={12}
          md={12}
          marginBottom={2}
        >
          <DictionaryAccordianWrapper>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="dictionary-content"
              id="dictionary-header"
            >
              <Typography component="span">
                <MenuBook /> Variables Dictionary
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ marginX: 'auto', maxHeight: '132px', overflowY: 'auto' }}>
              {dictionary && dictionary.length && (
                <Grid
                  container
                  spacing={{ xs: 1, md: 2 }}
                  xs={4}
                  sm={8}
                  md={12}
                >
                  {dictionary
                    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                    .map((variable, index) => (
                      <GridItem
                        key={index}
                        item
                        xs={4}
                        sm={4}
                        md={4}
                        textAlign="center"
                      >
                        {variable}
                      </GridItem>
                    ))}
                </Grid>
              )}
            </AccordionDetails>
          </DictionaryAccordianWrapper>
        </Grid>

        <Grid
          item
          xs={12}
          md={2}
        >
          <FormPart config={delayFormConfig} />
        </Grid>

        <Grid
          item
          xs={12}
          md={10}
          position="relative"
        >
          {values.workflowIds.map((value, index) => (
            <Grid
              key={index}
              container
            >
              <Grid
                item
                xs={11}
              >
                <StyledFormPart>
                  <FormPart
                    key={index}
                    config={getWorkflowFormConfig(value, index, workflowTree, setFieldValue)}
                  />
                </StyledFormPart>
                <DropdownsWrapper>
                  {!values.workflowIds[index] && errors.workflowIds && (
                    <ErrorWrapper top="191px">{errors.workflowIds}</ErrorWrapper>
                  )}
                  {!values.workflowSteps[index] && errors.workflowSteps && (
                    <ErrorWrapper
                      top="191px"
                      left="580px"
                    >
                      {errors.workflowSteps}
                    </ErrorWrapper>
                  )}
                </DropdownsWrapper>
              </Grid>

              <Grid
                item
                xs={1}
                display="flex"
              >
                {index !== 0 && (
                  <IconButton
                    data-action="delete-step"
                    color="primary"
                    title="Delete Workflow"
                    onClick={() => onRemoveWorkflowStep(index)}
                  >
                    <Delete />
                  </IconButton>
                )}
                {index === values.workflowIds.length - 1 && (
                  <IconButton
                    data-action="add-step"
                    color="primary"
                    title="Add Workflow"
                    disabled={isDisableNewWorkflowStep}
                    onClick={() => onAddWorkflowStep()}
                  >
                    <Add />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}
        </Grid>

        <Grid
          item
          xs={12}
        >
          <Grid container>
            <Grid
              item
              xs={11}
            >
              <FormPart config={statesFormConfig} />
            </Grid>
            <Grid
              item
              xs={1}
              display="flex"
              justifyContent="center"
            >
              <IconButton
                data-action="select-all-states"
                color="primary"
                title="Select All States"
                onClick={() => onSelectAllStates(true)}
              >
                <DoneAll />
              </IconButton>
              <IconButton
                data-action="deselect-all-states"
                color="primary"
                title="Deselect All States"
                onClick={() => onSelectAllStates(false)}
              >
                <RemoveDone />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
        >
          <Grid container>
            <Grid
              item
              xs={11}
            >
              <FormPart config={leadSourcesFormConfig} />
            </Grid>
            <Grid
              item
              xs={1}
              display="flex"
              justifyContent="center"
            >
              <IconButton
                data-action="select-all-sources"
                color="primary"
                title="Select All Sources"
                onClick={() => onSelectAllSources(true)}
              >
                <DoneAll />
              </IconButton>
              <IconButton
                data-action="deselect-all-sources"
                color="primary"
                title="Deselect All Sources"
                onClick={() => onSelectAllSources(false)}
              >
                <RemoveDone />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          justifySelf="flex-end"
          xs={2}
        >
          <CancelButton
            fullWidth
            onClick={handleReset}
          />
        </Grid>

        <Grid
          item
          justifySelf="flex-end"
          xs={2}
        >
          <SaveButton
            fullWidth
            disabled={!dirty || isSubmitting}
          />
        </Grid>
      </Grid>
    </Form>
  );
};
